import { Row, Col } from 'antd';
import { FormikProps } from 'formik';
import { Select } from 'formik-antd';
import bankersRounding from 'bankers-rounding';
import { useEffect } from 'react';
import { has, isNil } from 'lodash';

import {
  FinancialInfoSchedulerModel,
  FinancialSchedulerFormValues,
  MultiRateFormScheduleValues,
} from '../FinancialInfoScheduleModal';
import FormItem from 'components/formik/FormItem';
import InputNumber from 'components/formik/InputNumber';
import {
  getFinancialWarningMessages,
  getOvertimeOptions,
} from 'utils/assignmentUtils';
import { AssignmentMultiRateViewModel, OvertimeEnum } from 'api/adminApiClient';
import { getDecimalSafeValue } from 'utils/mathUtils';
import { CustomerMspOrVmsOverviewDto } from 'api/backofficeApiClient';
import {
  DT_PAY_RATE_MULTIPLIER,
  FinancialInfoCalculator,
  OVERTIME_MULTIPLIER,
} from '../../AssignmentDataModal/FinancialInfo/calculationUtils';
import { SimplePriceAgreementW2Info } from '../../AssignmentDataModal/FinancialInfo';
import { MULTI_RATES_KEY } from 'hooks/useFinancialInfoTabs';
import { findPTOMultiRate } from 'utils/multiRates/multiRatesUtils';

const FIELD_NAMES = {
  DT_PAY_RATE: 'financialInfoScheduler.dtPayRate',
  PROFIT_RATE: 'financialInfoScheduler.profitRate',
  PAY_RATE: 'financialInfoScheduler.payRate',
  BILL_RATE: 'financialInfoScheduler.billRate',
  OVERTIME: 'financialInfoScheduler.overtime',
  OVERTIME_BILL_RATE: 'financialInfoScheduler.overtimeBillRate',
  OVERTIME_PAY_RATE: 'financialInfoScheduler.overtimePayRate',
  DT_BILL_RATE: 'financialInfoScheduler.dtBillRate',
};

type Props = {
  formik: FormikProps<FinancialSchedulerFormValues>;
  mspOrVmsOverviewInfo?: CustomerMspOrVmsOverviewDto;
  w2InfoByWorkSiteWorkLocation?: SimplePriceAgreementW2Info;
  workStateLocation?: number;
  isW2Assignment: boolean;
  formRef: any;
  ptoMultiRate?: AssignmentMultiRateViewModel;
};

export default function FinancialInfoScheduleSection(props: Props) {
  const {
    w2InfoByWorkSiteWorkLocation,
    workStateLocation,
    mspOrVmsOverviewInfo,
    isW2Assignment,
    formik,
    // We should use formRef instead of formik
    // to get the current state of the form during any changes in the values in the fields
    formRef,
    ptoMultiRate,
  } = props;
  const isDisabledFields = !formik.values?.isCanUpdateScheduler;

  const setFieldValues = (field: string, value: any, touched: boolean) => {
    const { setFieldValue, setFieldTouched } = formik;

    setFieldValue(field, value);
    setFieldTouched(field, touched);
  };

  const calculateOvertimePayRate = () => {
    setTimeout(() => {
      if (!formRef.current) return;

      const { values } =
        formRef.current as FormikProps<FinancialSchedulerFormValues>;

      const overtimePayRate = values.financialInfoScheduler?.overtimePayRate;

      if (isNil(overtimePayRate) || overtimePayRate === OvertimeEnum.Exempt)
        return;

      const payRate = values?.financialInfoScheduler?.payRate || 0;
      const value = bankersRounding(payRate * OVERTIME_MULTIPLIER, 2);
      setFieldValues(FIELD_NAMES.OVERTIME_PAY_RATE, value, true);
    }, 0);
  };

  const calculateDTPayRate = () => {
    setTimeout(() => {
      if (!formRef.current) return;

      const { values } =
        formRef.current as FormikProps<FinancialSchedulerFormValues>;
      const { overtime, payRate } = values?.financialInfoScheduler || {};

      if (overtime === OvertimeEnum.Exempt) return;

      const value = bankersRounding((payRate || 0) * DT_PAY_RATE_MULTIPLIER, 2);
      setFieldValues(FIELD_NAMES.DT_PAY_RATE, value, true);
    }, 0);
  };

  useEffect(() => {
    has(formik.values?.financialInfoScheduler, 'profitRate') &&
      calculateProfitRateValue(false);
    // eslint-disable-next-line
  }, []);

  const financialWarningMessages = getFinancialWarningMessages({
    mspVmsValues: mspOrVmsOverviewInfo,
    hasW2InfoByWorkSiteWorkLocation:
      !!w2InfoByWorkSiteWorkLocation ||
      isNil(workStateLocation) ||
      !isW2Assignment,
  });

  const financialWarningMessageExists = !!financialWarningMessages.length;

  const getSafeComputedValue = (value: number) => {
    return financialWarningMessageExists ? 0 : getDecimalSafeValue(value);
  };

  const calculatePayRatePTOMultiRateValue = (
    ptoMultiRateTouched: boolean = true,
  ) => {
    setTimeout(() => {
      if (!formRef.current) return null;

      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialSchedulerFormValues>;

      if (!values.multiRates?.length) return null;

      const { ptoMultiRateIndex, ptoMultiRateObject } = findPTOMultiRate(
        values.multiRates,
      );

      const hasPayRateCustomLogic = (
        ptoMultiRateObject as MultiRateFormScheduleValues
      )?.payCodesInfo?.[0]?.hasPayRateCustomLogic;

      if (
        ptoMultiRateIndex === -1 ||
        isNil(ptoMultiRateObject) ||
        hasPayRateCustomLogic
      )
        return null;

      const fieldName = `${MULTI_RATES_KEY}[${ptoMultiRateIndex}].payCodesInfo[0].payRate`;
      const ptoPayTypeValue = FinancialInfoCalculator.calculatePayRatePTOValue(
        values.financialInfoScheduler?.payRate,
      );

      setFieldValue(fieldName, ptoPayTypeValue);
      setFieldTouched(fieldName, ptoMultiRateTouched, false);
    }, 0);
  };

  const calculatePayRateChange = () => {
    calculateProfitRateValue();
    calculateDTPayRate();
    calculateOvertimePayRate();
  };

  const handleBasePayRateChanged = () => {
    calculatePayRateChange();
    calculatePayRatePTOMultiRateValue();
  };

  const calculateProfitRateValue = (profitRateTouched: boolean = true) => {
    setTimeout(() => {
      if (!formRef.current) return;
      const { values } =
        formRef.current as FormikProps<FinancialSchedulerFormValues>;

      const { billRate, payRate } = values.financialInfoScheduler || {};

      // Reset profitRate if billRate ot payRate is not provided
      const shouldResetValue = isNil(billRate) || isNil(payRate);
      const profitRateValue = FinancialInfoCalculator.getProfitRateValue(
        values.financialInfoScheduler as FinancialInfoSchedulerModel,
        mspOrVmsOverviewInfo,
      );

      const safeValue = getSafeComputedValue(profitRateValue);

      setFieldValues(
        FIELD_NAMES.PROFIT_RATE,
        shouldResetValue ? 0 : safeValue,
        profitRateTouched,
      );
    }, 0);
  };

  const mbpPortionFormValue = getSafeComputedValue(
    FinancialInfoCalculator.getMbpPortionValue(
      formik.values.financialInfoScheduler?.payRate,
      formik.values.financialInfoScheduler?.mbpRate,
    ),
  );

  const calculateBIllRatePTOMultiRateValue = (
    ptoMultiRateTouched: boolean = true,
  ) => {
    setTimeout(() => {
      if (!formRef.current) return null;

      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialSchedulerFormValues>;

      if (!values.multiRates?.length) return null;

      const { ptoMultiRateIndex, ptoMultiRateObject } = findPTOMultiRate(
        values.multiRates,
      );

      const hasBillRateCustomLogic = (
        ptoMultiRateObject as MultiRateFormScheduleValues
      )?.payCodesInfo?.[0]?.hasBillRateCustomLogic;

      if (
        ptoMultiRateIndex === -1 ||
        isNil(ptoMultiRateObject) ||
        hasBillRateCustomLogic
      )
        return null;

      const memberBilledIndicator = ptoMultiRate?.memberBilledIndicator;

      const fieldName = `${MULTI_RATES_KEY}[${ptoMultiRateIndex}].payCodesInfo[0].billRate`;
      const ptoPayTypeValue = FinancialInfoCalculator.calculateBillRatePTOValue(
        values.financialInfoScheduler?.billRate,
        mbpPortionFormValue,
        memberBilledIndicator,
      );

      setFieldValue(fieldName, ptoPayTypeValue);
      setFieldTouched(fieldName, ptoMultiRateTouched, false);
    }, 0);
  };

  const handleBillRateChanged = () => {
    calculateProfitRateValue();
    calculateBIllRatePTOMultiRateValue(false);
  };

  return (
    <Row gutter={40}>
      {formik.values?.financialInfoScheduler?.hasOwnProperty('payRate') && (
        <>
          <Col xs={24} md={12}>
            <FormItem label="Base pay rate *" name={FIELD_NAMES.PAY_RATE}>
              <InputNumber
                disabled={isDisabledFields}
                name={FIELD_NAMES.PAY_RATE}
                placeholder="0.00"
                className="full-width"
                precision={2}
                inputPrefix={'$'}
                onValueChange={() => handleBasePayRateChanged()}
              />
            </FormItem>
          </Col>
        </>
      )}
      {formik.values?.financialInfoScheduler?.hasOwnProperty('billRate') && (
        <>
          <Col xs={24} md={12}>
            <FormItem label="Bill rate *" name={FIELD_NAMES.BILL_RATE}>
              <InputNumber
                disabled={isDisabledFields}
                name={FIELD_NAMES.BILL_RATE}
                placeholder="0.00"
                className="full-width"
                precision={2}
                inputPrefix={'$'}
                onValueChange={() => handleBillRateChanged()}
              />
            </FormItem>
          </Col>
        </>
      )}
      {(formik.values?.financialInfoScheduler?.hasOwnProperty('billRate') ||
        formik.values?.financialInfoScheduler?.hasOwnProperty('payRate')) && (
        <Col xs={24} md={12}>
          <FormItem label="Profit rate" name={FIELD_NAMES.PROFIT_RATE}>
            <InputNumber
              disabled
              allowNegative
              name={FIELD_NAMES.PROFIT_RATE}
              placeholder="0.00"
              className="full-width"
              precision={2}
            />
          </FormItem>
        </Col>
      )}
      {formik.values?.financialInfoScheduler?.hasOwnProperty('overtime') && (
        <Col xs={24} md={12}>
          <FormItem name={FIELD_NAMES.OVERTIME} label="Overtime *">
            <Select
              name={FIELD_NAMES.OVERTIME}
              placeholder="Select overtime type"
              className="full-width"
              disabled
            >
              {getOvertimeOptions().map((overtime) => (
                <Select.Option
                  key={`select-overtime-${overtime.value}`}
                  value={overtime.value || ''}
                >
                  {overtime.label}
                </Select.Option>
              ))}
            </Select>
          </FormItem>
        </Col>
      )}
      {formik.values?.financialInfoScheduler?.hasOwnProperty(
        'overtimeBillRate',
      ) && (
        <Col xs={24} md={12}>
          <FormItem
            label="OT bill rate *"
            name={FIELD_NAMES.OVERTIME_BILL_RATE}
          >
            <InputNumber
              disabled={
                isDisabledFields ||
                formik.values?.financialInfoScheduler?.overtime ===
                  OvertimeEnum.Exempt
              }
              name={FIELD_NAMES.OVERTIME_BILL_RATE}
              placeholder="0.00"
              className="full-width"
              precision={2}
              inputPrefix={'$'}
            />
          </FormItem>
        </Col>
      )}
      {formik.values?.financialInfoScheduler?.hasOwnProperty(
        'overtimePayRate',
      ) && (
        <Col xs={24} md={12}>
          <FormItem label="OT pay rate *" name={FIELD_NAMES.OVERTIME_PAY_RATE}>
            <InputNumber
              disabled={isDisabledFields}
              name={FIELD_NAMES.OVERTIME_PAY_RATE}
              placeholder="0.00"
              className="full-width"
              precision={2}
              inputPrefix={'$'}
            />
          </FormItem>
        </Col>
      )}
      {formik.values?.financialInfoScheduler?.hasOwnProperty('dtBillRate') && (
        <Col xs={24} md={12}>
          <FormItem label="DT Bill rate *" name={FIELD_NAMES.DT_BILL_RATE}>
            <InputNumber
              disabled={
                isDisabledFields ||
                formik.values?.financialInfoScheduler?.overtime ===
                  OvertimeEnum.Exempt
              }
              placeholder="0.00"
              name={FIELD_NAMES.DT_BILL_RATE}
              className="full-width"
              precision={2}
              inputPrefix={'$'}
            />
          </FormItem>
        </Col>
      )}
    </Row>
  );
}
